import { useMemo } from 'react';
import { useBookkeepingTransactions } from '~/services/bookkeeping/bookkeeping-transactions';
import { BookkeepingTransaction } from '~/types/general-bookkeeping';
import { isLessThanZero, isMoreThanZero } from '~/utils/money';

interface SuggestionParams {
  transactionDescription: string;
}

export enum Side {
  DEBIT = 'DARE',
  CREDIT = 'AVERE',
}

interface Entry {
  side: Side;
  accountId: string;
}

interface AccountSuggestion {
  entries: Entry[];
}

/**
 * Normalizes a string for matching purposes.
 *
 * This function performs the following steps:
 * 1. Converts the string to lowercase to remove case sensitivity.
 * 2. Applies Unicode normalization form 'NFKD' to decompose characters (separating diacritics).
 * 3. Removes diacritic marks (accents) using the Unicode Diacritic category.
 * 4. Replaces all non-alphanumeric characters (except spaces) with a space.
 * 5. Collapses multiple spaces into a single space.
 *
 * @param raw - The raw input string.
 * @returns The normalized string.
 */
const normalizeDescription = (raw: string): string => {
  return (raw || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const useBookkeepingAccountSuggestion = ({ transactionDescription }: SuggestionParams) => {
  const { data, isLoading } = useBookkeepingTransactions({ transactionDescription });

  const suggestion: AccountSuggestion | null = useMemo(() => {
    const desc = normalizeDescription(transactionDescription);
    if (!desc) return null;

    const pages = data?.pages ?? [];
    const bookkeepingTransactions: BookkeepingTransaction[] = pages.flatMap((p) => p.transactions);

    for (const bt of bookkeepingTransactions) {
      if (!bt.transactions[0]) continue;
      const txDesc = normalizeDescription(bt.transactions[0].description);
      if (txDesc !== desc) continue;
      if (bt.bookkeepingEntries.length === 0 || bt.bookkeepingEntries.length > 2) continue;

      let debitId: string | null = null;
      let creditId: string | null = null;
      for (const bookkeepingEntry of bt.bookkeepingEntries) {
        if (isLessThanZero(bookkeepingEntry.amount) && debitId === null) {
          debitId = bookkeepingEntry.bookkeepingAccount.id;
        } else if (isMoreThanZero(bookkeepingEntry.amount) && creditId === null) {
          creditId = bookkeepingEntry.bookkeepingAccount.id;
        }
      }

      if (debitId && creditId) {
        const entries: Entry[] = [];
        if (debitId) entries.push({ side: Side.DEBIT, accountId: debitId });
        if (creditId) entries.push({ side: Side.CREDIT, accountId: creditId });
        return { entries };
      }
    }

    return null;
  }, [data, transactionDescription]);

  return {
    isLoading,
    suggestion,
  };
};
